import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Play, Image, Film, Search, Filter, Eye } from 'lucide-react';
import Card from '../components/UI/Card';
import ContactPreview from '../components/Home/ContactPreview';

const Media: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const categories = [
    { id: 'all', name: 'All Media' },
    { id: 'videos', name: 'Videos' },
    { id: 'installations', name: 'Installations' },
    { id: 'festivals', name: 'Festivals' },
    { id: 'campaigns', name: 'Campaigns' },
    { id: 'documentaries', name: 'Documentaries' }
  ];

  const mediaItems = [
    {
      id: 1,
      title: 'Global Maritime Summit 2024 - Official Recap',
      category: 'videos',
      type: 'Video',
      duration: '5:32',
      views: '125K',
      thumbnail: 'https://images.unsplash.com/photo-1591115765373-5207764f72e7?auto=format&fit=crop&w=600&q=80',
      description: 'Comprehensive recap of India\'s largest maritime sector event featuring key highlights and delegate insights.',
      year: '2024',
      tags: ['Government', 'Maritime', 'Conference']
    },
    {
      id: 2,
      title: 'IFFI 2024 - Celebrity Interviews',
      category: 'videos',
      type: 'Video Series',
      duration: '15:45',
      views: '89K',
      thumbnail: 'https://images.unsplash.com/photo-1489599904821-34f03d0c0837?auto=format&fit=crop&w=600&q=80',
      description: 'Exclusive interviews with international film celebrities at the International Film Festival of India.',
      year: '2024',
      tags: ['Entertainment', 'Film', 'Celebrity']
    },
    {
      id: 3,
      title: 'Digital Art Installation - Tech Summit',
      category: 'installations',
      type: 'Interactive Installation',
      duration: null,
      views: '45K',
      thumbnail: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=600&q=80',
      description: 'Immersive digital art installation showcasing the future of technology at Bengaluru Tech Summit.',
      year: '2024',
      tags: ['Technology', 'Interactive', 'Digital Art']
    },
    {
      id: 4,
      title: 'Goa Tourism Campaign - Paradise Reimagined',
      category: 'campaigns',
      type: 'Commercial Campaign',
      duration: '2:30',
      views: '200K',
      thumbnail: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=600&q=80',
      description: 'Award-winning tourism campaign showcasing Goa\'s diverse attractions and cultural heritage.',
      year: '2024',
      tags: ['Tourism', 'Goa', 'Culture']
    },
    {
      id: 5,
      title: 'Aqua Goa Documentary - Ocean to Plate',
      category: 'documentaries',
      type: 'Documentary',
      duration: '28:15',
      views: '67K',
      thumbnail: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?auto=format&fit=crop&w=600&q=80',
      description: 'Documentary exploring Goa\'s fishing industry and sustainable aquaculture practices.',
      year: '2024',
      tags: ['Documentary', 'Fishing', 'Sustainability']
    },
    {
      id: 6,
      title: 'Sunburn Festival 2024 - After Movie',
      category: 'festivals',
      type: 'Festival Film',
      duration: '8:42',
      views: '340K',
      thumbnail: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?auto=format&fit=crop&w=600&q=80',
      description: 'High-energy after movie capturing the essence of Asia\'s premier electronic dance music festival.',
      year: '2024',
      tags: ['Music', 'Festival', 'EDM']
    },
    {
      id: 7,
      title: 'Corporate Holographic Display',
      category: 'installations',
      type: 'Holographic Installation',
      duration: null,
      views: '23K',
      thumbnail: 'https://images.unsplash.com/photo-1518709268805-4e9042af2176?auto=format&fit=crop&w=600&q=80',
      description: 'Cutting-edge holographic product display for major company product launch.',
      year: '2024',
      tags: ['Corporate', 'Holographic', 'Product Launch']
    },
    {
      id: 8,
      title: 'Agricultural Innovation Showcase',
      category: 'videos',
      type: 'Educational Video',
      duration: '12:18',
      views: '78K',
      thumbnail: 'https://images.unsplash.com/photo-1625246333195-78d9c38ad449?auto=format&fit=crop&w=600&q=80',
      description: 'Educational content showcasing modern agricultural techniques and sustainable farming practices.',
      year: '2024',
      tags: ['Agriculture', 'Education', 'Innovation']
    }
  ];

  const filteredMedia = mediaItems.filter(item => {
    const matchesCategory = activeCategory === 'all' || item.category === activeCategory;
    const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const getIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'video':
      case 'video series':
      case 'commercial campaign':
      case 'documentary':
      case 'festival film':
      case 'educational video':
        return Play;
      case 'interactive installation':
      case 'holographic installation':
        return Image;
      default:
        return Film;
    }
  };

  return (
    <>
      <Helmet>
        <title>Media | Mirchi Republic</title>
        <meta name="description" content="Explore our media portfolio including videos, installations, festival content, campaigns, and documentaries across various industries." />
      </Helmet>

      <div className="pt-20">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-br from-accent-1 to-accent-2 text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-black/20" />
          <div className="container-grid relative z-10">
            <div className="col-span-12 text-center">
              <motion.h1
                className="text-5xl lg:text-6xl font-bold mb-6"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                Media Gallery
              </motion.h1>
              <motion.p
                className="text-xl lg:text-2xl opacity-90 max-w-3xl mx-auto"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                Discover our creative portfolio spanning videos, installations, documentaries, and festival productions that bring stories to life.
              </motion.p>
            </div>
          </div>
        </section>

        {/* Filters */}
        <section className="py-12 bg-white dark:bg-dark-bg border-b border-neutral-200 dark:border-neutral-700">
          <div className="container-grid">
            <div className="col-span-12">
              {/* Search */}
              <div className="mb-8">
                <div className="relative max-w-md mx-auto lg:mx-0">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-neutral-400" />
                  <input
                    type="text"
                    placeholder="Search media..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-accent-1 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Category Filters */}
              <div className="flex flex-wrap justify-center lg:justify-start gap-3">
                {categories.map((category) => (
                  <motion.button
                    key={category.id}
                    onClick={() => setActiveCategory(category.id)}
                    className={`chip transition-all duration-200 ${
                      activeCategory === category.id
                        ? 'bg-accent-1 text-white'
                        : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 hover:bg-accent-1/10'
                    }`}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {category.name}
                  </motion.button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Media Grid */}
        <section className="py-20 bg-neutral-50 dark:bg-neutral-900">
          <div className="container-grid">
            <div className="col-span-12">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredMedia.map((item, index) => {
                  const IconComponent = getIcon(item.type);
                  return (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                    >
                      <Card hover className="h-full overflow-hidden group">
                        <div className="relative">
                          <img
                            src={item.thumbnail}
                            alt={item.title}
                            className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-105"
                          />
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                            <IconComponent className="w-12 h-12 text-white" />
                          </div>
                          <div className="absolute top-4 left-4">
                            <span className="chip bg-accent-1 text-white">
                              {item.type}
                            </span>
                          </div>
                          <div className="absolute top-4 right-4">
                            <span className="chip bg-white/90 text-neutral-900">
                              {item.year}
                            </span>
                          </div>
                          {item.duration && (
                            <div className="absolute bottom-4 right-4">
                              <span className="chip bg-black/70 text-white text-xs">
                                {item.duration}
                              </span>
                            </div>
                          )}
                        </div>
                        
                        <div className="p-6 space-y-4">
                          <div>
                            <h3 className="text-xl font-semibold mb-2 line-clamp-2">
                              {item.title}
                            </h3>
                            <p className="text-neutral-600 dark:text-neutral-400 text-sm line-clamp-3">
                              {item.description}
                            </p>
                          </div>
                          
                          <div className="flex items-center justify-between text-sm text-neutral-600 dark:text-neutral-400">
                            <div className="flex items-center space-x-2">
                              <Eye className="w-4 h-4" />
                              <span>{item.views} views</span>
                            </div>
                          </div>
                          
                          <div className="flex flex-wrap gap-1">
                            {item.tags.slice(0, 3).map((tag, tagIndex) => (
                              <span key={tagIndex} className="chip bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 text-xs">
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
              
              {filteredMedia.length === 0 && (
                <div className="text-center py-12">
                  <Filter className="w-16 h-16 text-neutral-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-neutral-600 dark:text-neutral-400 mb-2">
                    No media found
                  </h3>
                  <p className="text-neutral-500 dark:text-neutral-500">
                    Try adjusting your search or filter criteria
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>

        <ContactPreview />
      </div>
    </>
  );
};

export default Media;